<template>
  <modal
    name="app-prompt-modal"
    height="auto"
    :adaptive="true"
    width="420"
    :click-to-close="false"
    @closed="onModalClosed"
  >
    <div class="app-prompt" role="dialog" :aria-labelledby="state.title ? 'app-prompt-title' : undefined">
      <h5 v-if="state.title" id="app-prompt-title" class="app-prompt__title">{{ state.title }}</h5>
      <p class="app-prompt__message">{{ state.message }}</p>
      <input
        v-if="state.mode === 'prompt'"
        ref="promptInput"
        v-model="inputValue"
        type="text"
        class="form-control form-control-sm app-prompt__input"
        :placeholder="state.inputPlaceholder"
        @keyup.enter="submitPrompt"
      />
      <div class="app-prompt__actions">
        <template v-if="state.mode === 'prompt'">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="finish(null)">
            {{ state.cancelText }}
          </button>
          <button type="button" class="btn btn-primary btn-sm" @click="submitPrompt">
            {{ state.confirmText }}
          </button>
        </template>
        <template v-else-if="state.mode === 'confirm'">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="finish(false)">
            {{ state.cancelText }}
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="state.danger ? 'btn-danger' : 'btn-primary'"
            @click="finish(true)"
          >
            {{ state.confirmText }}
          </button>
        </template>
        <button v-else type="button" class="btn btn-primary btn-sm" @click="finish(true)">
          {{ state.okText }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { registerAppPromptHost, getAppPromptHost } from '@/utils/appPromptHost'

const emptyState = () => ({
  mode: 'alert',
  title: '',
  message: '',
  confirmText: '',
  cancelText: '',
  okText: '',
  danger: false,
  inputPlaceholder: '',
})

export default {
  name: 'AppPromptHost',
  data() {
    return {
      state: emptyState(),
      inputValue: '',
      pending: null,
    }
  },
  mounted() {
    registerAppPromptHost(this)
  },
  beforeDestroy() {
    if (getAppPromptHost() === this) registerAppPromptHost(null)
  },
  methods: {
    confirm(message, options = {}) {
      return this.open('confirm', message, options)
    },
    alert(message, options = {}) {
      return this.open('alert', message, options)
    },
    prompt(message, options = {}) {
      return this.open('prompt', message, options)
    },
    open(mode, message, options) {
      if (this.pending) {
        this.finish(false)
      }
      const i18n = this.$i18n
      const t = (key, fallback) => (i18n && i18n.te(key) ? i18n.t(key) : fallback)
      this.state = {
        mode,
        title: options.title || '',
        message: message || '',
        confirmText: options.confirmText || t('common.confirm_action', 'Confirm'),
        cancelText: options.cancelText || t('common.cancel', 'Cancel'),
        okText: options.okText || t('common.close_modal', 'Close'),
        danger: !!options.danger,
        inputPlaceholder: options.inputPlaceholder || '',
      }
      this.inputValue = mode === 'prompt' ? String(options.defaultValue ?? '') : ''
      return new Promise((resolve) => {
        this.pending = resolve
        this.$modal.show('app-prompt-modal')
        if (mode === 'prompt') {
          this.$nextTick(() => {
            const el = this.$refs.promptInput
            if (el) {
              el.focus()
              el.select()
            }
          })
        }
      })
    },
    submitPrompt() {
      this.finish(this.inputValue)
    },
    finish(result) {
      this.$modal.hide('app-prompt-modal')
      if (this.pending) {
        const resolve = this.pending
        this.pending = null
        resolve(result)
      }
    },
    onModalClosed() {
      if (this.pending) {
        const resolve = this.pending
        this.pending = null
        if (this.state.mode === 'confirm') resolve(false)
        else if (this.state.mode === 'prompt') resolve(null)
        else resolve(undefined)
      }
      this.state = emptyState()
      this.inputValue = ''
    },
  },
}
</script>

<style scoped>
.app-prompt {
  padding: 1.25rem 1.35rem 1.1rem;
}

.app-prompt__title {
  font-size: 1.05rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.app-prompt__input {
  margin-bottom: 1rem;
}

.app-prompt__message {
  margin-bottom: 1.1rem;
  color: #444;
  line-height: 1.5;
  white-space: pre-wrap;
}

.app-prompt__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
</style>
